import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material';
import { ActivatedRoute } from '@angular/router';
import { interval, Subscription, timer } from 'rxjs';
import { switchMap, takeUntil, takeWhile } from 'rxjs/operators';
import { mapApiProductNameToDisplayName } from '../../../../core/_config/product.constants';
import { InventoryHttpService } from '../../../../core/inventory';
import { AugerPurgeCommandResult, AugerPurgeHttpService } from '../../../../core/settings/_services/auger-purge-http.service';
import {
	AUGER_PURGE_FIELDS,
	AUGER_PURGE_MESSAGES,
	AugerPurgeCanister,
	AugerPurgeCanisterDraft,
	AugerPurgeCommand,
	AugerPurgeConfig,
	AugerPurgeDraft,
	AugerPurgeEvaluation,
	AugerPurgeField,
	AugerPurgeFieldKey,
	AugerPurgePatch,
	AugerPurgeRequestError,
	augerPurgeStatusLabel,
	cleaningCycleHintForInput,
	describeCommandPayload,
	draftFromConfig,
	evaluateDraft,
	formatEnabled,
	isPendingAugerPurgeStatus,
	splitServerErrors,
	unreportedFieldTooltip
} from '../../../../core/settings/_services/auger-purge.util';
import { MixpanelService } from '../../../../services/mixpanel.service';
import { MachineRouteService } from '../machine-route.service';
import { AugerPurgeConfirmDialogComponent } from './auger-purge-confirm-dialog.component';

type AugerPurgePhase = 'idle' | 'sending' | 'waiting' | 'applied' | 'not_confirmed' | 'failed';

@Component({
	selector: 'kt-auger-purge',
	templateUrl: './auger-purge.component.html',
	styleUrls: ['./auger-purge.component.scss']
})
export class AugerPurgeComponent implements OnInit, OnDestroy {
	readonly fields = AUGER_PURGE_FIELDS;
	readonly unknownTooltip = AUGER_PURGE_MESSAGES.unknownTooltip;
	readonly basisDefaultTooltip = AUGER_PURGE_MESSAGES.basisDefaultTooltip;
	readonly intervalDefaultTooltip = AUGER_PURGE_MESSAGES.intervalDefaultTooltip;
	readonly unreportedTooltip = unreportedFieldTooltip;
	readonly statusLabel = augerPurgeStatusLabel;
	readonly describeChange = describeCommandPayload;
	readonly purgeOptions = [
		{ label: 'On', value: 'on' },
		{ label: 'Off', value: 'off' }
	];

	machineId = '';
	config: AugerPurgeConfig = null;
	draft: AugerPurgeDraft = null;
	isLoading = false;
	loadError = '';
	phase: AugerPurgePhase = 'idle';
	statusMessage = '';
	formError = '';
	serverFieldErrors: { [key: string]: string } = {};
	private productsBySlot: { [slot: number]: string } = {};

	private destroyed = false;
	private pollSubscription: Subscription = null;
	private lastPatch: AugerPurgePatch = null;
	private lastSummary: string[] = [];

	constructor(
		private route: ActivatedRoute,
		private machineRouteService: MachineRouteService,
		private http: AugerPurgeHttpService,
		private inventoryHttp: InventoryHttpService,
		private dialog: MatDialog,
		private cdr: ChangeDetectorRef,
		private mixpanelService: MixpanelService
	) {}

	ngOnInit(): void {
		const machine = this.machineRouteService.getMachine(this.route);
		if (!machine) {
			this.loadError = 'Open this page from a machine.';
			return;
		}
		this.machineId = String(machine.machine_id || '');
		this.mixpanelService.track(this.mixpanelService.PAGE_VIEW, {
			machine_id: this.machineId,
			machine_name: machine.machine_name,
			machine_serial_no: machine.serial_no
		});
		this.loadConfig(false);
		this.loadProducts();
	}

	ngOnDestroy(): void {
		this.destroyed = true;
		this.stopWatching();
	}

	get formLocked(): boolean {
		return this.phase === 'sending' || this.phase === 'waiting';
	}

	get canSend(): boolean {
		if (!this.config || !this.draft || this.formLocked || this.formError || Object.keys(this.serverFieldErrors).length) {
			return false;
		}
		const evaluation = evaluateDraft(this.config, this.draft);
		return !evaluation.errors.length && !!evaluation.patch;
	}

	productName(slot: number): string {
		return this.productsBySlot[slot] || '';
	}

	reportedCanister(slot: number): AugerPurgeCanister {
		if (!this.config) {
			return null;
		}
		return (this.config.canisters || []).filter(canister => canister.slot === slot)[0] || null;
	}

	fieldError(slot: number | string, key: string): string {
		const field = slot + '.' + key;
		if (this.serverFieldErrors[field]) {
			return this.serverFieldErrors[field];
		}
		if (!this.config || !this.draft) {
			return '';
		}
		const match = evaluateDraft(this.config, this.draft).errors.filter(error => error.field === field)[0];
		return match ? match.message : '';
	}

	enabledError(): string {
		if (this.serverFieldErrors.enabled) {
			return this.serverFieldErrors.enabled;
		}
		if (!this.config || !this.draft) {
			return '';
		}
		const match = evaluateDraft(this.config, this.draft).errors.filter(error => error.field === 'enabled')[0];
		return match ? match.message : '';
	}

	unknownField(slot: number, key: AugerPurgeFieldKey): boolean {
		const canister = this.reportedCanister(slot);
		if (!canister) {
			return true;
		}
		return canister[key] === null || canister[key] === undefined;
	}

	fieldTooltip(row: AugerPurgeCanisterDraft, key: AugerPurgeFieldKey): string {
		if (this.unknownField(row.slot, key) && !row[key]) {
			return this.unreportedTooltip(key);
		}
		if (key === 'frequency_cycles') {
			return this.cycleHint(row);
		}
		return '';
	}

	cycleHint(row: AugerPurgeCanisterDraft): string {
		if (!this.config) {
			return '';
		}
		const canister = this.reportedCanister(row.slot);
		const interval = canister ? this.reportedInterval(canister.cleaning_interval) : null;
		return cleaningCycleHintForInput(this.config.cleaning_basis, interval, row.frequency_cycles) || '';
	}

	intervalText(slot: number): string {
		const canister = this.reportedCanister(slot);
		const value = canister ? this.reportedInterval(canister.cleaning_interval) : null;
		if (value === null) {
			return '';
		}
		const basis = this.config && this.config.cleaning_basis;
		if (basis === 'drinks') {
			return value + (value === 1 ? ' drink' : ' drinks');
		}
		if (basis === 'minutes') {
			return value + (value === 1 ? ' minute' : ' minutes');
		}
		return String(value);
	}

	isFrequencyOff(row: AugerPurgeCanisterDraft): boolean {
		return String(row.frequency_cycles || '').trim() === '0';
	}

	enabledLabel(): string {
		return formatEnabled(this.config ? this.config.enabled : null);
	}

	basisLabel(): string {
		if (!this.config || !this.config.cleaning_basis) {
			return 'Unknown';
		}
		return this.config.cleaning_basis;
	}

	onEnabledChange(value: 'on' | 'off' | null): void {
		if (!this.draft) {
			return;
		}
		if (value === null && this.config && (this.config.enabled === true || this.config.enabled === false)) {
			this.draft.enabled = this.config.enabled ? 'on' : 'off';
		} else {
			this.draft.enabled = value;
		}
		this.clearServerErrors();
	}

	onDraftChange(): void {
		this.clearServerErrors();
	}

	onFieldInput(row: AugerPurgeCanisterDraft, key: AugerPurgeFieldKey, event: Event): void {
		const input = event.target as HTMLInputElement;
		const digits = input.value.replace(/\D/g, '').replace(/^0+(?=\d)/, '');
		if (input.value !== digits) {
			input.value = digits;
		}
		row[key] = digits;
		this.onDraftChange();
	}

	inputWidth(row: AugerPurgeCanisterDraft, field: AugerPurgeField): string {
		const value = String(row[field.key] || '');
		if (!value) {
			return '7.5ch';
		}
		return value.length + 'ch';
	}

	canStep(row: AugerPurgeCanisterDraft, field: AugerPurgeField, delta: number): boolean {
		const raw = String(row[field.key] || '').trim();
		if (!raw) {
			return delta > 0;
		}
		const value = Number(raw);
		if (!Number.isFinite(value)) {
			return false;
		}
		return delta > 0 ? value < field.max : value > field.min;
	}

	stepField(row: AugerPurgeCanisterDraft, field: AugerPurgeField, delta: number): void {
		if (this.formLocked || !this.canStep(row, field, delta)) {
			return;
		}
		const raw = String(row[field.key] || '').trim();
		const step = field.step || 1;
		let next = raw ? Number(raw) + (delta * step) : Math.max(field.min, step);
		if (next < field.min) {
			next = field.min;
		}
		if (next > field.max) {
			next = field.max;
		}
		row[field.key] = String(next);
		this.onDraftChange();
	}

	onSend(): void {
		if (!this.config || !this.draft || this.formLocked) {
			return;
		}
		const evaluation = evaluateDraft(this.config, this.draft);
		if (evaluation.errors.length || !evaluation.patch) {
			this.cdr.detectChanges();
			return;
		}
		this.confirmAndSend(evaluation);
	}

	sendAgain(): void {
		if (!this.lastPatch || this.formLocked) {
			return;
		}
		this.confirmAndSend({
			patch: this.lastPatch,
			errors: [],
			summary: this.lastSummary
		});
	}

	private confirmAndSend(evaluation: AugerPurgeEvaluation): void {
		const dialogRef = this.dialog.open(AugerPurgeConfirmDialogComponent, {
			width: '480px',
			data: { summaryLines: evaluation.summary }
		});
		dialogRef.afterClosed().subscribe(result => {
			if (this.destroyed || !result || !result.confirmed) {
				return;
			}
			this.lastPatch = evaluation.patch;
			this.lastSummary = evaluation.summary.slice();
			this.sendPatch(evaluation.patch);
		});
	}

	private sendPatch(patch: AugerPurgePatch): void {
		this.phase = 'sending';
		this.statusMessage = 'Sending…';
		this.formError = '';
		this.cdr.detectChanges();
		this.http.setConfig(patch).subscribe({
			next: (result: AugerPurgeCommandResult) => {
				if (this.destroyed) {
					return;
				}
				if (result.status === 'acked') {
					this.phase = 'applied';
					this.statusMessage = AUGER_PURGE_MESSAGES.applied;
					this.loadConfig(true);
					return;
				}
				if (result.status === 'failed') {
					this.phase = 'failed';
					this.statusMessage = AUGER_PURGE_MESSAGES.unreachable;
					this.cdr.detectChanges();
					return;
				}
				this.watchCommand(result.command_id);
			},
			error: (error: AugerPurgeRequestError) => {
				if (this.destroyed) {
					return;
				}
				this.applyRequestError(error);
			}
		});
	}

	private watchCommand(commandId: string): void {
		this.stopWatching();
		this.phase = 'waiting';
		this.statusMessage = AUGER_PURGE_MESSAGES.waiting;
		this.cdr.detectChanges();
		this.pollSubscription = interval(3000).pipe(
			takeUntil(timer(60000)),
			switchMap(() => this.http.getCommand(commandId)),
			takeWhile((command: AugerPurgeCommand) => isPendingAugerPurgeStatus(command.status), true)
		).subscribe({
			next: (command: AugerPurgeCommand) => this.onCommandUpdate(command),
			error: () => {
				if (this.destroyed) {
					return;
				}
				this.phase = 'failed';
				this.statusMessage = 'Could not check whether the machine applied the change.';
				this.cdr.detectChanges();
			},
			complete: () => {
				if (this.destroyed || this.phase !== 'waiting') {
					return;
				}
				this.phase = 'not_confirmed';
				this.statusMessage = AUGER_PURGE_MESSAGES.notConfirmed;
				this.cdr.detectChanges();
			}
		});
	}

	private onCommandUpdate(command: AugerPurgeCommand): void {
		if (this.destroyed || isPendingAugerPurgeStatus(command.status)) {
			return;
		}
		if (command.status === 'acked') {
			this.phase = 'applied';
			this.statusMessage = AUGER_PURGE_MESSAGES.applied;
			this.loadConfig(true);
			return;
		}
		this.phase = 'failed';
		this.statusMessage = command.error || AUGER_PURGE_MESSAGES.unreachable;
		this.cdr.detectChanges();
	}

	private loadProducts(): void {
		this.inventoryHttp.getMachineInventory({ machine_id: this.machineId }).subscribe({
			next: inventory => {
				if (this.destroyed) {
					return;
				}
				const products: { [slot: number]: string } = {};
				const rows = Array.isArray(inventory) ? inventory : [];
				rows.forEach(row => {
					const slot = Number(row && row.slotNo);
					const name = row && row.name ? String(row.name).trim() : '';
					if (!slot || !name || name.toUpperCase() === 'EMPTY') {
						return;
					}
					products[slot] = mapApiProductNameToDisplayName(name);
				});
				this.productsBySlot = products;
				this.cdr.detectChanges();
			},
			error: () => {
				// The purge settings still load if inventory is unavailable.
			}
		});
	}

	private loadConfig(quiet: boolean): void {
		if (!quiet) {
			this.isLoading = true;
			this.loadError = '';
		}
		this.http.getConfig(this.machineId).subscribe({
			next: config => {
				if (this.destroyed) {
					return;
				}
				this.config = config;
				this.draft = draftFromConfig(config);
				this.serverFieldErrors = {};
				this.formError = '';
				this.isLoading = false;
				this.cdr.detectChanges();
			},
			error: (error: AugerPurgeRequestError) => {
				if (this.destroyed) {
					return;
				}
				this.isLoading = false;
				this.loadError = error.message || 'Failed to load auger purge settings.';
				this.cdr.detectChanges();
			}
		});
	}

	private applyRequestError(error: AugerPurgeRequestError): void {
		const split = splitServerErrors(error.details, (this.lastPatch && this.lastPatch.canisters) || []);
		this.serverFieldErrors = split.fields;
		if (error.status === 400 && (split.general || Object.keys(split.fields).length)) {
			this.phase = 'idle';
			this.statusMessage = '';
			this.formError = split.general || 'Check the highlighted fields.';
		} else {
			this.phase = 'failed';
			this.statusMessage = error.message || AUGER_PURGE_MESSAGES.unreachable;
			this.formError = split.general;
		}
		this.cdr.detectChanges();
	}

	private clearServerErrors(): void {
		if (!Object.keys(this.serverFieldErrors).length && !this.formError) {
			return;
		}
		this.serverFieldErrors = {};
		this.formError = '';
	}

	private reportedInterval(value: number | null | undefined): number | null {
		if (value === null || value === undefined) {
			return null;
		}
		const numeric = Number(value);
		return Number.isFinite(numeric) ? numeric : null;
	}

	private stopWatching(): void {
		if (this.pollSubscription) {
			this.pollSubscription.unsubscribe();
			this.pollSubscription = null;
		}
	}
}
